import { StandardEvents } from '@shopify/events';

class ProductConfigurator extends HTMLElement {
  #abortController = new AbortController();
  #variantRules = {};

  connectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#variantRules = this.#readVariantRules();
    this.addEventListener('change', this.#handleConfigurationChange, { signal });
    this.addEventListener('invalid', this.#handleInvalid, { capture: true, signal });
    this.#form?.addEventListener('submit', this.#handleSubmit, { capture: true, signal });

    const eventTarget = this.closest('.shopify-section, dialog');
    eventTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    this.#syncRules();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #handleConfigurationChange = () => {
    this.#syncRules();
  };

  #handleInvalid = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.#showValidationError();
  };

  #handleSubmit = (event) => {
    const form = this.#form;
    if (!(form instanceof HTMLFormElement) || event.target !== form) return;

    if (!form.checkValidity()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      form.reportValidity();
      this.#showValidationError();
      this.#syncValidityState();
    }
  };

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Apply immediately from the event payload. The form variant ID may still
    // point at the previous selection until the section update resolves.
    const selectedOptions = Array.isArray(event.selectedOptions) ? event.selectedOptions : [];
    const upstream = selectedOptions.find(({ name }) => name === this.dataset.upstreamOptionName)?.value;
    if (upstream) this.#applyPowerRule(this.#isTwentyAmpUpstream(upstream, this.#currentVariantRules));

    event.promise
      ?.then(async () => {
        await Promise.resolve();
        this.#syncRules();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-configurator] Variant update failed', error);
      });
  };

  #syncRules() {
    this.#syncPowerRule();
    this.#syncPhonoRule();
    this.#syncCuringWaiver();
    this.#syncValidityState();
  }

  #syncPowerRule() {
    const selectedUpstream = this.#selectedNativeOption(this.dataset.upstreamOptionName);
    this.#applyPowerRule(this.#isTwentyAmpUpstream(selectedUpstream, this.#currentVariantRules));
  }

  /**
   * Prefer stable variant rule keys; fall back to the selected upstream label so
   * the UI still updates before the form ID catches up.
   * @param {string | undefined} upstream
   * @param {string[]} rules
   */
  #isTwentyAmpUpstream(upstream, rules) {
    if (Array.isArray(rules) && rules.includes('power_20a')) return true;
    return /20\s*a/i.test(upstream ?? '');
  }

  /** @param {boolean} isTwentyAmp */
  #applyPowerRule(isTwentyAmp) {
    const standardGroup = this.querySelector('[data-power-standard]');
    const twentyAmpGroup = this.querySelector('[data-power-twenty-amp]');
    if (!(standardGroup instanceof HTMLElement) || !(twentyAmpGroup instanceof HTMLElement)) return;

    this.#setChoiceGroupActive(standardGroup, !isTwentyAmp);
    this.#setChoiceGroupActive(twentyAmpGroup, isTwentyAmp);
  }

  #syncPhonoRule() {
    const selectedSource = this.querySelector('[data-phono-source]:checked');
    const stageInputs = Array.from(this.querySelectorAll('[data-phono-stage]'));
    if (!(selectedSource instanceof HTMLInputElement) || stageInputs.length === 0) return;

    const sourceIsRca = selectedSource.dataset.choiceKey === 'rca';

    for (const input of stageInputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      const isAllowed = !sourceIsRca || input.dataset.choiceKey === 'rca';
      input.disabled = !isAllowed;
      input.required = isAllowed;
      input.closest('label')?.toggleAttribute('hidden', !isAllowed);
      if (!isAllowed) input.checked = false;
    }

    if (!stageInputs.some((input) => input instanceof HTMLInputElement && !input.disabled && input.checked)) {
      const firstAllowed = stageInputs.find((input) => input instanceof HTMLInputElement && !input.disabled);
      if (firstAllowed instanceof HTMLInputElement) firstAllowed.checked = true;
    }
  }

  #syncCuringWaiver() {
    const selectedCuring = this.querySelector('[data-curing-choice]:checked');
    const waiver = this.querySelector('[data-curing-waiver]');
    const waiverInput = waiver?.querySelector('input[type="checkbox"]');
    if (!(selectedCuring instanceof HTMLInputElement) || !(waiver instanceof HTMLElement)) return;
    if (!(waiverInput instanceof HTMLInputElement)) return;

    const waiverRequired = selectedCuring.dataset.choiceKey === 'no';
    waiver.hidden = !waiverRequired;
    waiverInput.disabled = !waiverRequired;
    waiverInput.required = waiverRequired;
    if (!waiverRequired) waiverInput.checked = false;
  }

  #setChoiceGroupActive(group, active) {
    group.hidden = !active;
    const inputs = Array.from(group.querySelectorAll('input'));

    for (const input of inputs) {
      input.disabled = !active;
      input.required = active;
      if (!active) input.checked = false;
    }

    if (active && !inputs.some((input) => input.checked)) {
      const firstInput = inputs[0];
      if (firstInput) firstInput.checked = true;
    }
  }

  #syncValidityState() {
    const form = this.#form;
    if (!form) return;

    const isValid = form.checkValidity();
    const productForm = form.closest('product-form-component');
    const acceleratedCheckout = productForm?.querySelector(
      '[ref="acceleratedCheckoutButtonContainer"], shopify-accelerated-checkout'
    );
    const stickyButton = this.closest('.shopify-section')?.querySelector(
      'sticky-add-to-cart [ref="addToCartButton"]'
    );

    if (acceleratedCheckout instanceof HTMLElement) {
      acceleratedCheckout.hidden = !isValid;
      acceleratedCheckout.inert = !isValid;
      acceleratedCheckout.dataset.configuratorDisabled = isValid ? 'false' : 'true';
      acceleratedCheckout.setAttribute('aria-disabled', isValid ? 'false' : 'true');
    }

    if (stickyButton instanceof HTMLButtonElement) {
      const variantAvailable = stickyButton.closest('sticky-add-to-cart')?.dataset.variantAvailable === 'true';
      stickyButton.disabled = !isValid || !variantAvailable;
      stickyButton.dataset.configuratorDisabled = isValid ? 'false' : 'true';
    }

    if (isValid) {
      const error = this.querySelector('[data-configurator-error]');
      if (error instanceof HTMLElement) error.hidden = true;
    }
  }

  #showValidationError() {
    const error = this.querySelector('[data-configurator-error]');
    if (error instanceof HTMLElement) error.hidden = false;
  }

  #readVariantRules() {
    const script = this.querySelector('[data-variant-rules]');
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return {};

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[product-configurator] Invalid variant rules JSON', error);
      return {};
    }
  }

  #selectedNativeOption(optionName) {
    const variantPicker = this.closest('.shopify-section, dialog')?.querySelector('variant-picker');
    const selectedOptions =
      typeof variantPicker?.getAllSelectedOptions === 'function' ? variantPicker.getAllSelectedOptions() : [];
    return selectedOptions.find(({ name }) => name === optionName)?.value;
  }

  get #currentVariantRules() {
    const variantId = this.#form?.querySelector('input[name="id"]')?.value;
    const rules = variantId ? this.#variantRules[variantId] : null;
    return Array.isArray(rules) ? rules : [];
  }

  get #form() {
    const nested = this.closest('form');
    return nested instanceof HTMLFormElement ? nested : null;
  }
}

if (!customElements.get('product-configurator')) {
  customElements.define('product-configurator', ProductConfigurator);
}
